// --- START OF FILE TrackingView.js ---

import React, { useState, useEffect, useRef } from 'react';
import { 
    Check, X, Shield, Sun, ArrowRightLeft, Ban, 
    Pause, Home, MessageSquare, Clock, 
    Hash, DollarSign, Target, 
    ShieldCheck, BarChart2, Hand, Flag, RotateCcw
} from 'lucide-react';
import { ErrorModal } from '../shared/ErrorModal';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { getWinningBall } from '../../utils/gameConfig';

// --- UI Constants & Helpers ---
const ballColors = {
  1: { bg: 'bg-yellow-400', text: 'text-black' },
  2: { bg: 'bg-blue-600', text: 'text-white' },
  3: { bg: 'bg-red-600', text: 'text-white' },
  4: { bg: 'bg-purple-600', text: 'text-white' },
  5: { bg: 'bg-orange-500', text: 'text-black' },
  6: { bg: 'bg-green-600', text: 'text-white' },
  7: { bg: 'bg-red-800', text: 'text-white' },
  8: { bg: 'bg-black', text: 'text-white' },
  9: { bg: 'bg-yellow-400', text: 'text-black', stripe: true },
  10: { bg: 'bg-blue-600', text: 'text-white', stripe: true },
};

const shotResults = [
    { value: 'made', label: 'Made', Icon: Check, color: 'bg-green-500', iconColor: 'text-white' },
    { value: 'miss', label: 'Miss', Icon: X, color: 'bg-red-500', iconColor: 'text-white' },
    { value: 'foul', label: 'Foul', Icon: Ban, color: 'bg-orange-500', iconColor: 'text-white' },
    { value: 'safety', label: 'Safety', Icon: Shield, color: 'bg-blue-500', iconColor: 'text-white' },
    { value: 'dry_break', label: 'Dry Break', Icon: Sun, color: 'bg-gray-500', iconColor: 'text-yellow-300' },
    { value: 'push_out', label: 'Push Out', Icon: ArrowRightLeft, color: 'bg-purple-500', iconColor: 'text-white' },
];

const shotTags = [
    { value: 'masse', label: 'Masse', emoji: '🎩' }, 
    { value: 'jump', label: 'Jump', emoji: '🦘' },
    { value: 'kick', label: 'Kick', emoji: '🦶' }, 
    { value: 'bank', label: 'Bank', emoji: '🏦' },
    { value: 'combo', label: 'Combo', emoji: '🎯' }, 
    { value: 'cut', label: 'Cut Shot', emoji: '📐' },
    { value: 'bridge', label: 'Bridge', emoji: '🌉' },
    { value: 'lucky', label: 'Lucky', emoji: '🍀' },
    { value: 'lucky_safe', label: 'Lucky Safe', emoji: '😇' },
    // --- MODIFICATION: Add new "failed_safety" tag ---
    { value: 'failed_safety', label: 'Failed Safety', emoji: '😬' },
    { value: 'win', label: 'Win', emoji: '🏁' },
    { value: 'lose', label: 'Lose', emoji: '💀' },
];

const distanceOptions = ['short', 'medium', 'long'];

const allTags = [
    { value: 'break', label: 'Break', emoji: '💥' }, 
    ...shotTags, 
    { value: 'break_and_run', label: 'Break & Run', emoji: '⚡️' }, 
    { value: 'ball_in_hand', label: 'Ball in Hand', emoji: '🖐️'}, 
    { value: 'money_ball_shot', label: 'Money Ball Shot', Icon: DollarSign, color: 'text-yellow-400' },
    { value: 'golden_break', label: 'Golden Break', emoji: '👑' }
];

const HideScrollbarStyle = () => (
  <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
);

const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const ShotDetailModal = ({ shot, matchData, onClose }) => {
    if (!shot) return null;

    const getPlayerNameByHandle = (handle) => {
        if (!matchData) return '...';
        return handle === 'player1' ? matchData.player1 : matchData.player2;
    };

    const resultInfo = shotResults.find(r => r.value === shot.result);
    
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-sm w-full mx-4 border border-slate-700 shadow-2xl animate-slide-up relative">
                 <button onClick={onClose} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white rounded-full hover:bg-slate-700 transition-colors"><X size={20} /></button>
                <h2 className="text-xl font-bold mb-4 text-center text-purple-400">Shot Details</h2>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Player:</span><span className="font-bold">{getPlayerNameByHandle(shot.player)}</span></div>
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Inning / Shot:</span><span className="font-bold">{shot.inning} / {shot.shotNumber}</span></div>
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Shot Duration:</span><span className="font-bold">{formatDuration(shot.duration)}</span></div>
                    <div className="flex justify-between items-center p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Result:</span><span className="font-bold flex items-center gap-2">{resultInfo?.Icon ? <resultInfo.Icon className="w-4 h-4" /> : null} {resultInfo?.label}</span></div>
                    {shot.ballsMade && shot.ballsMade.length > 0 && (
                        <div className="flex justify-between p-2 bg-slate-700/50 rounded-md">
                            <span className="text-slate-400">Balls Made:</span>
                            <span className="font-bold">{shot.ballsMade.join(', ')}</span>
                        </div>
                    )}
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md">
                        <span className="text-slate-400">Distance:</span>
                        <span className="font-bold capitalize">{shot.distance || 'N/A'}</span>
                    </div>
                    {shot.notes && (
                        <div className="pt-2">
                            <h3 className="text-slate-300 mb-1 font-semibold">Notes:</h3>
                            <p className="text-sm text-white/80 p-2 bg-slate-700/50 rounded-md">{shot.notes}</p>
                        </div>
                    )}
                    {shot.tags && shot.tags.length > 0 && (
                       <div className="pt-2">
                           <h3 className="text-slate-300 mb-2 text-center font-semibold">Tags</h3>
                           <div className="flex flex-wrap justify-center gap-2">
                               {[...new Set(shot.tags)].map(tag => {
                                   const tagInfo = allTags.find(t => t.value === tag);
                                   return tagInfo ? (
                                     <div key={tag} className="bg-slate-700 rounded-full px-3 py-1 text-xs flex items-center gap-2">
                                         {tagInfo.emoji && <span>{tagInfo.emoji}</span>}
                                         {tagInfo.Icon && <tagInfo.Icon size={14} className={tagInfo.color || ''} />}
                                         <span className="font-medium">{tagInfo.label}</span>
                                     </div>
                                   ) : null;
                               })}
                           </div>
                       </div>
                    )}
                </div>
            </div>
        </div>
    );
};


const groupShotsByInning = (shots) => {
    if (!shots) return {};
    return shots.reduce((acc, shot) => {
        (acc[shot.inning] = acc[shot.inning] || []).push(shot);
        return acc;
    }, {});
};

export const TrackingView = ({
    currentUser,
    matchData, 
    gameState, 
    shotLog,
    currentShotData, 
    setCurrentShotData,
    logShot, 
    handleUndo, 
    handlePushOutDecision,
    navigateTo, 
    abandonMatch,
    requestTimeout,
    acceptTimeout,
    declineTimeout,
    endTimeout,
}) => {
    const { ballsRemaining } = gameState;
    
    const [selectedShot, setSelectedShot] = useState(null);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });
    const [confirmationModal, setConfirmationModal] = useState({ show: false, title: '', message: '', onConfirm: () => {} });
    const [distanceEnabled, setDistanceEnabled] = useState(false);
    const [shotTimer, setShotTimer] = useState('00:00');
    const [totalMatchTime, setTotalMatchTime] = useState('00:00');
    const [timeoutRemaining, setTimeoutRemaining] = useState(0);
    const [isLuckySafe, setIsLuckySafe] = useState(false);
    // --- MODIFICATION: Add state for failed safety toggle ---
    const [isFailedSafety, setIsFailedSafety] = useState(false);

    const shotLogRef = useRef(null);

    const isMyTurn = gameState.currentPlayerId === currentUser.uid;
    const canControl = matchData.controlMode === 'shared' || isMyTurn;

    useEffect(() => {
        let timer;
        if (gameState.startTime) {
            timer = setInterval(() => {
                const now = new Date();
                const matchStart = new Date(gameState.startTime);
                setTotalMatchTime(formatDuration(Math.floor((now - matchStart) / 1000)));
                if (gameState.currentShotStartTime) {
                    const shotStart = new Date(gameState.currentShotStartTime);
                    setShotTimer(formatDuration(Math.floor((now - shotStart) / 1000)));
                }
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [gameState.startTime, gameState.currentShotStartTime]);

    useEffect(() => {
        if (shotLogRef.current) {
            shotLogRef.current.scrollTop = shotLogRef.current.scrollHeight;
        }
    }, [shotLog]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (gameState.currentShotStartTime) {
                const now = new Date();
                const start = new Date(gameState.currentShotStartTime);
                const diff = Math.floor((now - start) / 1000);
                setShotTimer(formatDuration(diff));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [gameState.currentShotStartTime]);

    // Timeout timer effect
    useEffect(() => {
        if (gameState.isTimeout && gameState.timeoutDuration > 0 && gameState.timeoutStartTime) {
            const interval = setInterval(() => {
                const now = new Date();
                const start = new Date(gameState.timeoutStartTime);
                const elapsed = Math.floor((now - start) / 1000);
                const remaining = Math.max(0, gameState.timeoutDuration - elapsed);
                setTimeoutRemaining(remaining);
                
                if (remaining <= 0) {
                    // Auto-end timeout when time is up
                    endTimeout();
                }
            }, 1000);

            return () => clearInterval(interval);
        } else {
            setTimeoutRemaining(0);
        }
    }, [gameState.isTimeout, gameState.timeoutDuration, gameState.timeoutStartTime, endTimeout]);

    const showError = (message) => setErrorModal({ show: true, message });

    // --- MODIFICATION: Update logShot to handle both new tags ---
    const handleLogShot = () => {
        if (!currentShotData.result) { showError('Please select a shot result.'); return; }
        if (currentShotData.result === 'made' && currentShotData.ballsMade.length === 0) { showError("You must select at least one ball for a 'Made' shot."); return; }
        
        let finalShotData = { ...currentShotData };
        if (currentShotData.result === 'miss' && isLuckySafe) {
            finalShotData.tags = [...finalShotData.tags, 'lucky_safe'];
        }
        if (currentShotData.result === 'safety' && isFailedSafety) {
            finalShotData.tags = [...finalShotData.tags, 'failed_safety'];
        }

        logShot(finalShotData);
        setCurrentShotData({ result: '', tags: [], ballsMade: [], distance: '', notes: '' });
        setIsLuckySafe(false);
        setIsFailedSafety(false); // Reset both toggles
    };
    
    const handleRequestAbandon = () => {
        setConfirmationModal({
            show: true,
            title: 'Forfeit Match?',
            message: 'Are you sure you want to end this match? This will result in a forfeit.',
            onConfirm: () => {
                setConfirmationModal({ show: false });
                abandonMatch();
            }
        });
    };

    const handleResultChange = (newResult) => {
        setCurrentShotData(prev => {
            const newState = { ...prev, result: newResult };
            const resultsThatClearBalls = ['miss', 'safety', 'dry_break'];
            if (resultsThatClearBalls.includes(newResult)) {
                newState.ballsMade = [];
            }
            return newState;
        });
        if (newResult !== 'miss') {
            setIsLuckySafe(false);
        }
        if (newResult !== 'safety') {
            setIsFailedSafety(false);
        }
    };

    const lastGameWinnerIndex = shotLog.map(s => s.isGameWinner).lastIndexOf(true);
    const currentGameShots = lastGameWinnerIndex === -1 ? shotLog : shotLog.slice(lastGameWinnerIndex + 1);
    const isBreakShot = currentGameShots.length === 0;
    const lastShot = shotLog.length > 0 ? shotLog[shotLog.length - 1] : null;
    const isPushOutAvailable = (matchData.gameType === '9-Ball' || matchData.gameType === '10-Ball') && currentGameShots.length === 1 && lastShot?.tags.includes('break');
    
    const isBallsMadeActive = 
        (matchData.gameType === '8-Ball' && ['made', 'foul'].includes(currentShotData.result)) ||
        (matchData.gameType === '9-Ball' && ['made', 'foul', 'push_out'].includes(currentShotData.result)) ||
        (matchData.gameType === '10-Ball' && ['made', 'foul', 'push_out', 'miss'].includes(currentShotData.result)) ||
        (isBreakShot && ['made', 'foul'].includes(currentShotData.result));

    const availableShotResults = shotResults.filter(result => {
        if (isBreakShot) return ['made', 'foul', 'dry_break'].includes(result.value);
        if (isPushOutAvailable && !gameState.isPushOutDecision) return ['made', 'miss', 'foul', 'safety', 'push_out'].includes(result.value);
        return !['dry_break', 'push_out'].includes(result.value);
    });
    
    const winningBall = getWinningBall(matchData.gameType);
    let isWinTagActive = false;
    if (currentShotData.ballsMade.includes(winningBall)) {
      if (matchData.gameType === '8-Ball') {
        // 8-ball must be called to win
        isWinTagActive = matchData.callEight;
      } else if (matchData.gameType === '9-Ball') {
        isWinTagActive = true;
      } else if (matchData.gameType === '10-Ball') {
        if (matchData.tenBallLast) {
          isWinTagActive = (ballsRemaining?.length === 1 && ballsRemaining[0] === 10);
        } else {
          isWinTagActive = true;
        }
      }
    }

    if (gameState.isPushOutDecision) {
        const decisionMakerName = gameState.currentPlayerId === matchData.player1Id ? matchData.player1 : matchData.player2;
        const lastShotResult = lastShot?.result;
        const decisionTitle = lastShotResult === 'push_out' ? 'Push Out Played' : 'Accidental Pot';
        const decisionText = lastShotResult === 'push_out' 
            ? `${decisionMakerName}, you have the option.`
            : `A ball was made on the miss. ${decisionMakerName}, you may take the shot or give it back.`;
        
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="max-w-md mx-auto w-full bg-slate-800/50 rounded-xl p-8 text-center border border-slate-700">
                     <h2 className="text-2xl font-bold mb-2">{decisionTitle}</h2>
                     <p className="text-slate-400 mb-6">{decisionText}</p>
                     {canControl ? (
                         <div className="space-y-3">
                            <button onClick={() => handlePushOutDecision(true)} className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-green-700 transition-colors">Take the Shot</button>
                            <button onClick={() => handlePushOutDecision(false)} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors">Return to Shooter</button>
                         </div>
                     ) : (
                        <p className="text-lg font-semibold animate-pulse">Waiting for opponent to decide...</p>
                     )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <HideScrollbarStyle />
            <ShotDetailModal shot={selectedShot} matchData={matchData} onClose={() => setSelectedShot(null)} />
            <ErrorModal show={errorModal.show} message={errorModal.message} onClose={() => setErrorModal({ show: false, message: '' })} />
            <ConfirmationModal show={confirmationModal.show} title={confirmationModal.title} message={confirmationModal.message} onConfirm={confirmationModal.onConfirm} onClose={() => setConfirmationModal({ show: false })} />
            
            <div className="max-w-6xl mx-auto pb-32">
                <header className="bg-slate-800/80 p-2 sm:p-3 sticky top-0 z-10 backdrop-blur-sm border-b border-slate-700">
                    <div className="flex justify-between items-center mb-2">
                        <button onClick={handleRequestAbandon} className="p-2 rounded-full hover:bg-slate-700 -ml-2" title="Forfeit Match"><Home className="w-5 h-5 text-slate-400 hover:text-white" /></button>
                        <div className="text-center">
                            <h2 className="text-lg font-bold">{matchData.gameType}</h2>
                            <p className="text-slate-400 text-xs">{`Race: ${matchData.player1RaceTo} - ${matchData.player2RaceTo}`}</p>
                        </div>
                         <button onClick={() => navigateTo('analytics')} className="p-2 rounded-full hover:bg-slate-700 -mr-2 lg:hidden" title="Analytics"><BarChart2 className="w-5 h-5 text-slate-400 hover:text-white" /></button>
                         <div className="w-9 h-9 hidden lg:block" />
                    </div>
                    <div className="bg-slate-800/70 p-3 rounded-xl shadow-lg mb-2">
                        <div className="flex justify-between items-center text-white">
                            <div className={`flex items-center gap-2 font-bold text-md sm:text-lg truncate transition-all ${gameState.currentPlayerId === matchData.player1Id ? 'text-green-400' : ''}`}>
                                <span>{matchData.player1}</span>
                                {matchData.player1IsProfile && <ShieldCheck className="w-5 h-5 text-green-400" title="Registered Player" />}
                            </div>
                            <div className="text-3xl sm:text-4xl font-bold mx-2">{gameState.player1Score} - {gameState.player2Score}</div>
                            <div className={`flex items-center gap-2 font-bold text-md sm:text-lg truncate text-right transition-all ${gameState.currentPlayerId === matchData.player2Id ? 'text-green-400' : ''}`}>
                                <span>{matchData.player2}</span>
                                {matchData.player2IsProfile && <ShieldCheck className="w-5 h-5 text-green-400" title="Registered Player" />}
                            </div>
                        </div>
                        <div className="text-center text-slate-400 text-xs mt-2 flex items-center justify-center gap-2"><Clock size={12} />{gameState.startTime ? <span>Total Match Time: {totalMatchTime}</span> : <span>Match timer starts on break</span>}</div>
                        
                        {/* Timeout Section */}
                        {gameState.isTimeout && (
                            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3 mt-2">
                                <div className="flex items-center justify-center gap-2 text-yellow-400 font-semibold mb-2">
                                    <Pause className="w-4 h-4" />
                                    Timeout in Progress
                                </div>
                                {gameState.timeoutDuration > 0 ? (
                                    <div className="text-center text-yellow-300 text-sm">
                                        Timeout active - {formatDuration(timeoutRemaining)} remaining
                                    </div>
                                ) : (
                                    <div className="text-center text-yellow-300 text-sm">
                                        {matchData.player2IsProfile 
                                            ? 'Waiting for opponent to accept/decline timeout'
                                            : 'Timeout requested'
                                        }
                                    </div>
                                )}
                                {/* Only show accept/decline buttons for registered opponents */}
                                {gameState.timeoutRequestedBy !== currentUser.uid && 
                                 gameState.timeoutDuration === 0 && 
                                 matchData.player2IsProfile && (
                                    <div className="flex gap-2 mt-2">
                                        <button
                                            onClick={acceptTimeout}
                                            className="flex-1 bg-green-600 text-white py-1.5 rounded text-sm font-medium hover:bg-green-700 transition-colors"
                                        >
                                            Accept
                                        </button>
                                        <button
                                            onClick={declineTimeout}
                                            className="flex-1 bg-red-600 text-white py-1.5 rounded text-sm font-medium hover:bg-red-700 transition-colors"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                )}
                                {gameState.timeoutDuration > 0 && (
                                    <button
                                        onClick={endTimeout}
                                        className="w-full bg-blue-600 text-white py-1.5 rounded text-sm font-medium hover:bg-blue-700 transition-colors mt-2"
                                    >
                                        End Timeout
                                    </button>
                                )}
                            </div>
                        )}
                        
                        <div className="flex justify-between items-center text-slate-400 text-xs mt-2 pt-2 border-t border-slate-700">
                            <div className="flex items-center gap-2"><span>Inning: <span className="font-bold text-white">{gameState.currentInning}</span></span><span>Shot: <span className="font-bold text-white">{gameState.currentShot}</span></span>{gameState.ballInHand && <div className="flex items-center gap-1 font-bold text-blue-400">BIH <Hand size={12}/></div>}</div>
                            <div className="flex items-center gap-1 font-mono text-white" title="Shot Timer"><Target size={12} className="text-green-400"/><span>{shotTimer}</span></div>
                        </div>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-8 p-3 sm:p-4">
                    <div className={`space-y-5 ${!canControl ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <h3 className="text-md font-semibold mb-2 flex items-center"><Target className="w-5 h-5 mr-2 text-green-400" />Shot Result</h3>
                            {isBreakShot && <div className="text-center mb-2 text-xs text-yellow-400 bg-yellow-400/10 py-1 rounded-md">Break Shot</div>}
                            {isPushOutAvailable && <div className="text-center mb-2 text-xs text-purple-400 bg-purple-400/10 py-1 rounded-md">Push Out Available</div>}
                            <div className={`grid gap-2 grid-cols-3`}>
                                {availableShotResults.map(result => (
                                    <button 
                                        key={result.value} 
                                        onClick={() => handleResultChange(result.value)} 
                                        className={`p-2 rounded-lg text-center transition-all flex flex-col items-center justify-center h-16 ${currentShotData.result === result.value ? `${result.color} text-white ring-2 ring-white/50 shadow-lg` : 'bg-slate-700 hover:bg-slate-600'}`}>
                                        <result.Icon className={`w-6 h-6 ${currentShotData.result !== result.value ? result.iconColor : 'text-white'}`} />
                                        <div className="font-medium text-xs mt-1">{result.label}</div>
                                    </button>
                                ))}
                            </div>
                            {currentShotData.result === 'miss' && (
                                <div className="flex items-center justify-center mt-3 bg-slate-700/50 p-2 rounded-lg">
                                    <label htmlFor="lucky-safe-toggle" className="text-sm font-medium text-slate-300 mr-3">Accidental Safe?</label>
                                    <button
                                        id="lucky-safe-toggle"
                                        type="button"
                                        onClick={() => setIsLuckySafe(prev => !prev)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isLuckySafe ? 'bg-green-500' : 'bg-slate-600'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isLuckySafe ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            )}
                            {/* --- MODIFICATION: Add conditional toggle for "Failed Safety" --- */}
                            {currentShotData.result === 'safety' && (
                                <div className="flex items-center justify-center mt-3 bg-slate-700/50 p-2 rounded-lg">
                                    <label htmlFor="failed-safety-toggle" className="text-sm font-medium text-slate-300 mr-3">Failed Safety? (Sell-out)</label>
                                    <button
                                        id="failed-safety-toggle"
                                        type="button"
                                        onClick={() => setIsFailedSafety(prev => !prev)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isFailedSafety ? 'bg-red-500' : 'bg-slate-600'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isFailedSafety ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between"><h3 className="text-md font-semibold flex items-center">📏 Shot Distance</h3><button onClick={() => setDistanceEnabled(on => !on)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${distanceEnabled ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${distanceEnabled ? 'translate-x-6' : 'translate-x-1'}`} /></button></div>
                            {distanceEnabled && (<div className="grid grid-cols-3 gap-2">{distanceOptions.map(distance => (<button key={distance} onClick={() => setCurrentShotData(prev => ({...prev,  distance: prev.distance === distance ? '' : distance}))} className={`py-2 px-3 rounded-lg text-sm font-medium transition-all capitalize ${currentShotData.distance === distance ? 'bg-blue-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}`}>{distance}</button>))}</div>)}
                        </div>
                        <div className={!isBallsMadeActive ? 'opacity-50' : ''}>
                            <h3 className="text-md font-semibold mb-2 flex items-center">🎱 Balls Made</h3>
                            <div className="flex space-x-2 overflow-x-auto py-2 -mx-3 px-3 hide-scrollbar">
                                {(ballsRemaining || []).map(ball => {
                                    const color = ballColors[ball];
                                    const isSelected = currentShotData.ballsMade.includes(ball);
                                    const isStriped = color?.stripe;
                                    const buttonBg = isStriped ? 'bg-white' : (color ? color.bg : 'bg-slate-800');
                                    const buttonText = color ? color.text : 'text-slate-600';
                                    const stripeBg = isStriped ? color.bg : '';
                                    return (<button key={ball} onClick={() => setCurrentShotData(prev => ({...prev, ballsMade: prev.ballsMade.includes(ball) ? prev.ballsMade.filter(b => b !== ball) : [...prev.ballsMade, ball].sort((a,b) => a-b) }))} disabled={!isBallsMadeActive} className={`relative w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-base transition-all overflow-hidden ${isSelected ? 'ring-4 ring-offset-2 ring-offset-slate-900 ring-white' : 'shadow-md'} ${!isBallsMadeActive ? 'cursor-not-allowed' : ''} ${buttonBg} ${buttonText}`}>{isStriped && (<span className={`absolute top-1/2 left-0 w-full h-[45%] ${stripeBg} -translate-y-1/2`} />)}<span className="relative">{ball}</span></button>);
                                })}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-md font-semibold mb-2 flex items-center"><Hash className="w-5 h-5 mr-2 text-purple-400" />Shot Type Tags</h3>
                            <div className="flex flex-wrap justify-center gap-2">
                                {shotTags.filter(tag => !['lucky_safe', 'failed_safety'].includes(tag.value)).map(tag => {
                                    const isWinButton = tag.value === 'win';
                                    const isLoseButton = tag.value === 'lose';
                                    const isDisabled = (isWinButton && !isWinTagActive) || 
                                                     (isLoseButton && !(matchData.gameType === '8-Ball' && currentShotData.ballsMade.includes(8)));
                                    
                                    return (
                                        <button 
                                            key={tag.value} 
                                            onClick={() => setCurrentShotData(prev => ({...prev, tags: prev.tags.includes(tag.value) ? prev.tags.filter(t => t !== tag.value) : [...prev.tags, tag.value]}))}
                                            disabled={isDisabled}
                                            className={`flex-shrink-0 w-16 p-1 rounded-lg text-center transition-all 
                                                ${currentShotData.tags.includes(tag.value) ? 'bg-purple-500 text-white ring-2 ring-purple-300' : 'bg-slate-700 hover:bg-slate-600'}
                                                ${isDisabled ? 'opacity-50 cursor-not-allowed hover:bg-slate-700' : ''}`}
                                        >
                                            <div className="text-base">{tag.emoji}</div>
                                            <div className="font-medium text-[10px] leading-tight mt-0.5">{tag.label}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-md font-semibold mb-2 flex items-center"><MessageSquare className="w-5 h-5 mr-2 text-blue-400" />Shot Notes (Optional)</h3>
                            <textarea value={currentShotData.notes} onChange={(e) => setCurrentShotData(prev => ({...prev, notes: e.target.value}))} rows="2" className="w-full p-2 rounded-lg bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="e.g., Missed position on the 7..."></textarea>
                        </div>
                    </div>

                    <div className="mt-6 lg:mt-0">
                        {shotLog.length > 0 ? (
                            <div className="bg-slate-800/50 p-3 sm:p-4 rounded-xl h-full">
                                <h3 className="text-md font-semibold mb-3 flex items-center"><Clock className="w-5 h-5 mr-2" /> Current Rack Log</h3>
                                <div ref={shotLogRef} className="max-h-64 lg:max-h-[calc(100vh-300px)] overflow-y-auto space-y-1 pr-2">
                                    {Object.entries(groupShotsByInning(currentGameShots)).reverse().map(([inning, shots]) => (
                                    <div key={inning} className="bg-slate-700/50 rounded-lg p-2 mb-2">
                                        <div className="font-bold text-xs text-green-400 mb-2">Inning {inning}</div>
                                        {shots.map(shot => {
                                        const resultInfo = shotResults.find(r => r.value === shot.result);
                                        const ResultIcon = resultInfo ? resultInfo.Icon : null;
                                        const playerName = shot.playerId === matchData.player1Id ? matchData.player1 : matchData.player2;
                                        return (
                                            <div key={shot.id} onClick={() => setSelectedShot(shot)} className="flex items-center justify-between py-1.5 text-xs border-b border-slate-600/50 last:border-b-0 cursor-pointer hover:bg-slate-600/50 rounded px-2 -mx-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0"><span className="w-5 text-center font-mono text-slate-400">#{shot.shotNumber}</span><span className="font-medium truncate">{playerName}</span></div>
                                                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                                    {shot.ballsMade.length > 0 && <div className="text-[10px] bg-yellow-400/20 text-yellow-300 px-1.5 py-0.5 rounded-full">{shot.ballsMade.join(',')}</div>}
                                                    {[...new Set(shot.tags)].map(tag => { 
                                                        const tagInfo = allTags.find(t => t.value === tag); 
                                                        if (!tagInfo) return null;
                                                        if (tagInfo.Icon) {
                                                            return <tagInfo.Icon key={tag} title={tagInfo.label} size={14} className={tagInfo.color || ''} />;
                                                        }
                                                        if (tagInfo.emoji) {
                                                            return <span key={tag} title={tagInfo.label} className="text-md">{tagInfo.emoji}</span>;
                                                        }
                                                        return null;
                                                    })}
                                                    {shot.notes && <MessageSquare size={12} className="text-blue-400"/>}
                                                    {shot.duration !== undefined && (<div className="flex items-center gap-1 text-[10px] text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded-full"><Clock size={10} /><span>{formatDuration(shot.duration)}</span></div>)}
                                                    {ResultIcon && <ResultIcon className={`w-4 h-4 ${resultInfo.iconColor}`} />}
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-800/50 p-8 rounded-xl h-full flex items-center justify-center">
                                <p className="text-slate-400">No shots logged for this rack yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <footer className="fixed bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-slate-700 z-20">
                <div className="max-w-xl mx-auto p-2">
                    <div className="space-y-2">
                        <button 
                            onClick={handleLogShot} 
                            disabled={!canControl || !currentShotData.result || (currentShotData.result === 'made' && currentShotData.ballsMade.length === 0)} 
                            className="w-full bg-green-600 text-white py-2.5 rounded-lg font-semibold text-base hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                        >
                            <Flag className="w-5 h-5 mr-2" /> 
                            {isMyTurn ? "Log Shot & Next" : "Waiting for Opponent..."}
                        </button>
                        <div className="grid grid-cols-3 gap-2">
                            <button onClick={handleUndo} disabled={!canControl || !gameState.historyStack || gameState.historyStack.length === 0} className="w-full bg-gray-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"><RotateCcw className="w-4 h-4 mr-2" /> Undo</button>
                            <button onClick={() => navigateTo('analytics')} className="w-full bg-purple-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-purple-700 transition-all flex items-center justify-center"><BarChart2 className="w-4 h-4 mr-2" /> Analytics</button>
                            <button 
                                onClick={requestTimeout} 
                                disabled={gameState.isTimeout} 
                                className="w-full bg-yellow-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                <Pause className="w-4 h-4 mr-2" /> 
                                {matchData.player2IsProfile ? 'Request Timeout' : 'Start Timeout'}
                            </button>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};